import {StrictMode, useCallback, useEffect, useMemo} from 'react';
import {Provider} from 'react-redux';
import {call, delay, fork, join, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {Route, Switch, useLocation, useRouteMatch} from "react-router-dom";
import styled, {ThemeProvider} from 'styled-components/macro';
import classNames from "classnames";
import {ChakraProvider, useColorMode} from "@chakra-ui/react"

import useTheme from './theme';
import {autoSignIn} from './api/sign_in';
import {AppContext, useLoggedIn, useServerEffect, useSettings} from './store';
import {useFeedback} from './store/feedback';
import {actions, bypassingReadyChecks, signalReady, useSagaEffect} from './store/sagas';
import {periodicEventChannel} from "./utils/channels";
import storage from "./utils/storage";
import {useModalStack} from "./utils/modal";
import {useWindowMessages} from "./utils/use_messages";
import PassiveLogin from "./components/passive_login";
import ScrollToTop from "./components/scroll_to_top";
import ErrorBoundary from "./components/error_boundary";
import chakraTheme from "./components/custom_theme";
import {loadUserProfile, saveSettings} from "./api/user";
import {selectLoggedIn} from "./store/selectors";

import './style/index.css';

import ReactGA from "react-ga4";

// Code-splitting is automated for `routes` directory
import Profile from "./routes/profile";
import AltPosts from "./routes/alt_posts";
import Listing from "./routes/listing";
import Search from './routes/search';
import Settings from "./routes/settings";
import Messages from "./routes/messages";
import MessageThread from "./routes/message";
import PostPage from "./routes/post";
import SavedFolder from "./routes/saved_folder";
import Menu from "./routes/menu";
import Wiki from "./routes/wiki";
import Stylesheet from "./routes/config_stylesheet";
import Queue from "./routes/queue";
import SetPostFlairs from "./routes/config/post_flairs";
import Comments from "./routes/comments";
import Filter from "./routes/config/filter";
import SidebarConfig from "./routes/config/sidebar";
import Logs from "./routes/logs";
import CommunitySettings from "./routes/config/settings";
import SetCommunityCreation from "./routes/admin/set_community_creation";
import {saveClientSettings} from "./store/client_settings";
import Communities from "./routes/communities";
import Feeds from "./routes/feeds";
import Rules from "./routes/config/rules";
import Bans from "./routes/ban";
import Moderators from "./routes/config/moderators";
import Blocks from "./routes/blocks";
import PageNotFound from "./routes/page_not_found";
import {loadAdConfigSaga} from "./api/ads";
import useGATag from "./utils/analytics";
import {clearApiCredentials, loadApiCredentials} from "./api/client";
import Modmail from "./routes/modmail";
import useVisitorId from "./utils/ad_utils";
import useRouteChange from "./utils/useRouteChange";
import {handleRouteChange} from "./utils/route_change";
import TrackingPixels from "./components/pixels/tracking_pixels";
import {themecolors} from "./utils/themecolors";

export function useGoogleAnalyticsSetup() {
    const initializeGA = () => {
        const loggedIn = useLoggedIn();
        ReactGA.initialize(useGATag());
        ReactGA.set({ dimension1: loggedIn ? 'LoggedIn': 'LoggedOut' });
    };

    initializeGA();
}

export const AppDiv = styled(({className, ...props}) => {
    const {theme} = useSettings();
    useGoogleAnalyticsSetup();
    return (
        <div className={classNames(className, `theme-${theme}`)} {...props}/>
    );
})`
  ${({theme}) => `
    color: ${theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
    background-color: ${theme.color.name === 'dark' ? themecolors.dark_background_1 : themecolors.light_input};
    
    .react-loading-skeleton {
        background-color: ${theme.skeleton.color};
        background-image: linear-gradient(
          90deg,
          ${theme.skeleton.color},
          ${theme.skeleton.highlightColor},
          ${theme.skeleton.color}
        );
    }
  `}
`;

function* serverStartUpSaga() {
    // Allow API calls to proceed.
    yield call(signalReady);
}

function failsafe(saga) {
    return saga;
    /*
    if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_STAGING) {
        return saga;
    }
    return function*() {
        try {
            yield call(saga);
        } catch {
            // silently ignored
        }
    };
    */
}

function* browserStartUpSaga() {

    if (module.hot?.data) {
        yield call(signalReady);
        return;
    }

    const tasks = [];

    try {
        tasks.push(yield fork(loadAdConfigSaga));
    } catch {
        console.log("failed to load ad config");
    }

    // Synchronize settings with storage and server.
    yield takeLatest('settingsLoaded', function* ({payload: {settings}}) {
        yield call([storage, 'setItem'], 'settings', JSON.stringify(settings));
    });
    let combinedChanges = {};
    yield takeLatest('settingsChanged', failsafe(function* ({payload: {changes}}) {
        Object.assign(combinedChanges, changes);
        const oldSettings = JSON.parse(yield call([storage, 'getItem'], 'settings') || 'null');
        yield call([storage, 'setItem'], 'settings', JSON.stringify({...oldSettings, ...combinedChanges}));
        if (yield select(selectLoggedIn)) {
            yield delay(100);
            yield call(saveSettings, combinedChanges);
        }
        combinedChanges = {};
    }));

    // Persist client side settings to local storage.
    yield takeLatest('clientSettingsChanged', function* () {
        const settings = yield select(({ clientSettings }) => clientSettings);
        yield call(saveClientSettings, settings);
    });

    // Persist client side settings to local storage.
    yield takeLatest('clientSettingsChanged', function* () {
        const settings = yield select(({ clientSettings }) => clientSettings);
        yield call(saveClientSettings, settings);
    });

    // Load communities in the background.
    yield takeEvery('communityListLoaded', function* ({payload: {sort, communities}}) {
        if (sort === 'communities') {
            yield call([storage, 'setItem'], 'communities', JSON.stringify(communities));
        }
    });

    // Clear localstorage on logout.
    yield takeEvery('logout', function* () {
        yield call([storage, 'clear']);
    });

    // Dispatch an action every minute.
    const minute = yield call(periodicEventChannel, 60 * 1000);
    yield takeEvery(minute, failsafe(function* () {
        try {
            yield actions.timeChanged(new Date());
            // Reload user profile to update unread message count.
            yield call(loadUserProfile);
        } catch {
            console.warn(`exception in 1min periodic task`);
        }
    }));

    // Allow API calls to proceed.
    yield call(signalReady);

    // Attempt to sign in and update profile/subscribed communities in the background.
    yield call(bypassingReadyChecks, function*() {
        let retries = 0;
        while (retries < 2) {
            if (!loadApiCredentials()) {
                yield call(autoSignIn);
            }
            try {
                yield call(loadUserProfile);
                return;
            } catch (e) {
                if (e.toString() === "Error: invalid user") {
                    yield call(clearApiCredentials);
                }
            }
            retries++;
        }
    });

    // Ensure background tasks are done before returning (not strictly necessary).
    yield join(tasks);
}

const ForbiddenPage = styled(({ className }) => <div className={className}>Forbidden</div>)`
  padding: 20px;
  background: white;
  font-size: 24px;
`;

const ModCheck = ({ children }) => {
    const match = useRouteMatch();
    const settings = useSettings();
    if (match.params.community && !settings?.moderates.includes(match.params.community)) {
        return <ForbiddenPage/>;
    }
    return children;
};

const MainRouter = () => {
    const feedback = useFeedback();
    useServerEffect(serverStartUpSaga);
    useSagaEffect(feedback.channel, browserStartUpSaga);

    const location = useLocation();

    useEffect(() => {
        try {
            const urlParams = new URLSearchParams(location.search);
            const referrer = urlParams.get('ref') || urlParams.get('utm_campaign');

            if (referrer) {
                localStorage.setItem('referrer', referrer);
            }
        } catch (error) {
            console.error('Error setting referrer in localStorage:', error);
        }
    }, [location.search]);

    const visitorId = useVisitorId();
    const { referrer: signupReferrer, username } = useSettings();

    const memoizedHandleRouteChange = useCallback(handleRouteChange(visitorId, 'pv', signupReferrer, username), [visitorId, signupReferrer]);

    useRouteChange(memoizedHandleRouteChange);

    return (
        <Switch>
            <Route exact path="/"><Listing sortBy='hot'/></Route>
            <Route path="/active"><Listing sortBy='active'/></Route>
            <Route path="/new"><Listing sortBy='new'/></Route>
            <Route path="/rising"><Listing sortBy='rising'/></Route>
            <Route path="/top"><Listing sortBy='top'/></Route>
            <Route path="/feed/:feedId/:feedName/new"><Listing customFeed sortBy='new' /></Route>
            <Route path="/feed/:feedId/:feedName/rising"><Listing customFeed sortBy='rising' /></Route>
            <Route path="/feed/:feedId/:feedName/active"><Listing customFeed sortBy='active' /></Route>
            <Route path="/feed/:feedId/:feedName/top"><Listing customFeed sortBy='top' /></Route>
            <Route path="/feed/:feedId/:feedName"><Listing customFeed sortBy='hot' /></Route>

            <Route path="/modmail"><Modmail /></Route>
            <Route path="/c/:community/modmail"><Modmail /></Route>

            <Route path={["/c/:community/config/stylesheet", "/config/stylesheet"]}>
                <Stylesheet />
            </Route>

            <Route path={["/c/:community/config/filter", "/config/filter"]}>
                <Filter />
            </Route>

            <Route path={["/c/:community/config/sidebar", "/config/sidebar"]}>
                <SidebarConfig />
            </Route>

            <Route path={["/c/:community/config/settings", "/config/settings"]}>
                <CommunitySettings />
            </Route>

            <Route path={["/c/:community/config/post-flairs", "/config/post-flairs"]}>
                <SetPostFlairs />
            </Route>

            <Route path={["/c/:community/config/moderators", "/config/moderators"]}>
                <Moderators />
            </Route>

            <Route path={["/c/:community/ban", "/ban"]}>
                <Bans />
            </Route>

            <Route path={["/c/:community/config/reports", "/config/reports"]}>
                <Rules />
            </Route>

            <Route path={["/c/:community/queue", "/queue"]}>
                <Queue />
            </Route>

            <Route path="/communities"><Communities /></Route>

            <Route path="/feeds"><Feeds /></Route>

            <Route path="/comments"><Comments /></Route>
            <Route path="/c/:community/comments"><Comments /></Route>
            <Route path="/c/:community/active"><Listing sortBy='active'/></Route>
            <Route path="/c/:community/new"><Listing sortBy='new'/></Route>
            <Route path="/c/:community/rising"><Listing sortBy='rising'/></Route>
            <Route path="/c/:community/top"><Listing sortBy='top'/></Route>
            <Route path={["/c/:community/logs", "/logs"]}><Logs logType='all' /></Route>
            <Route path="/c/:community/ban-logs"><Logs logType='bans' /></Route>
            <Route path="/wiki/:slug"><Wiki/></Route>
            <Route path="/c/:community/wiki/:slug"><Wiki/></Route>
            <Route path="/wiki/"><Menu type="wiki_index"/></Route>
            <Route path="/c/:community/wiki/"><Menu type="wiki_index"/></Route>
            <Route path="/c/:community/p/:uuid/:title/c/:commentUuid/"><PostPage/></Route>
            <Route path="/c/:community/p/:uuid/"><PostPage/></Route>
            <Route path="/p/:uuid/:title/c/:commentUuid/"><PostPage/></Route>
            <Route path="/p/:uuid/"><PostPage/></Route>
            <Route path="/c/:community/submit"><Menu type="create_post"/></Route>
            <Route path="/c/:community"><Listing sortBy='hot'/></Route>
            <Route path="/u/:user"><Profile/></Route>
            <Route path="/user/:user"><Profile/></Route>
            <Route path="/hidden"><AltPosts type='hidden' title='Hidden'/></Route>
            <Route path="/upvoted"><AltPosts type='upvoted' title='Upvoted'/></Route>
            <Route path="/saved/:folder"><SavedFolder/></Route>
            <Route path="/search"><Search/></Route>
            <Route path="/settings"><Settings/></Route>
            <Route path="/messages"><Messages/></Route>
            <Route path="/message/:id/"><MessageThread/></Route>
            <Route path="/create"><Menu type="create_community"/></Route>
            <Route path="/submit"><Menu type="create_post"/></Route>
            <Route path="/test-menu-onboarding"><Menu type="onboarding"/></Route>
            <Route path="/test-menu-community-onboarding"><Menu type="community_onboarding"/></Route>
            <Route path="/send"><Menu type="create_message"/></Route>
            <Route path="/pro"><Menu type="pro"/></Route>
            <Route path="/saved"><Menu type="saved"/></Route>
            <Route path="/blocks"><Blocks /></Route>
            <Route path="/admin/set-community-creation"><SetCommunityCreation /></Route>

            <Route path="/reset"><Menu type="reset_password"/></Route>
            <Route path="/change"><Menu type="change_password"/></Route>

            <Route path="*"><PageNotFound /></Route>
        </Switch>
    );
};

const Themed = ({children}) => {
    const settings = useSettings();
    const theme = useTheme(settings.theme);
    const { colorMode, toggleColorMode } = useColorMode();
    useEffect(() => {
        if (settings.theme !== colorMode) {
            toggleColorMode();
        }
    }, [settings.theme, colorMode])
    return (
        <ThemeProvider theme={theme}>
            {children}
        </ThemeProvider>
    );
};

const App = ({sagaMiddleware, store, actions, readyCh}) => {
    const [modalElement, modal] = useModalStack();
    const appContext = useMemo(() => ({
        store, actions, sagaMiddleware, modal, readyCh
    }), [store, actions, sagaMiddleware, modal, readyCh]);
    useWindowMessages(actions.windowMessageReceived);

    const {host} = location;
    const isScored = host.includes('scored.co') || host.includes('communities.win') ||
        host.includes('localhost');

    return (
        <StrictMode>
            <AppContext.Provider value={appContext}>
                <Provider store={store}>
                    <ErrorBoundary>
                        <ChakraProvider resetCSS theme={chakraTheme} >
                            <Themed>
                                <AppDiv id='app' className={`csr-mdrn ${isScored ? 'first-party' : 'third-party'}`}>
                                    <ScrollToTop/>
                                    <MainRouter/>
                                    {modalElement}
                                    <PassiveLogin/>
                                    <TrackingPixels />
                                </AppDiv>
                            </Themed>
                        </ChakraProvider>
                    </ErrorBoundary>
                </Provider>
            </AppContext.Provider>
        </StrictMode>
    );
};

export default App;
